import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchMe, type MeResponse } from './auth.js';

/**
 * The single `/auth/me` query, shared by route guarding (`RequireAuth`) and the shell
 * chrome (display name, roles). One query key means one cache entry — a `logout`
 * mutation invalidates this key and every consumer re-renders from the same source.
 *
 * This query doubles as the CSRF cookie's bootstrap request: `internal-api`'s CSRF
 * middleware issues `__Host-ms_csrf` on ANY request that lacks it, safe methods
 * included (`api/src/platform/http/csrf.ts`) — so mounting this at the app root means
 * the cookie usually already exists by the time a staff member submits the
 * login form. `lib/api-client.ts`'s own retry-once handles the remaining case (cookies
 * cleared between mount and submit, or a very fast first paint).
 */
export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

export function useSession(): UseQueryResult<MeResponse> {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    retry: false,
    staleTime: 60_000,
  });
}
